import React, { useState, useRef, useEffect, useMemo } from 'react';
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface Option {
  id: string;
  name: string;
  description?: string;
}

interface MultiSelectProps {
  options: Option[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  placeholder: string;
  label: string;
  error?: string;
  hint?: string;
  showBulkActions?: boolean;
  emptyMessage?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedIds,
  onChange,
  placeholder,
  label,
  error,
  hint,
  showBulkActions = false,
  emptyMessage = 'Nenhum item encontrado',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOptions = useMemo(
    () => options.filter(option => selectedIds.includes(option.id)),
    [options, selectedIds]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node))
      {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return options.filter(option => {
      if (selectedIds.includes(option.id))
      {
        return false;
      }

      if (!term)
      {
        return true;
      }

      const haystack = `${option.name} ${option.description ?? ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [options, selectedIds, searchTerm]);

  const handleSelect = (option: Option) => {
    onChange([...selectedIds, option.id]);
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const handleRemove = (optionId: string) => {
    onChange(selectedIds.filter(id => id !== optionId));
  };

  const handleSelectAll = () => {
    onChange(options.map(option => option.id));
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange([]);
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const dropdownMessage = (() => {
    if (options.length === 0)
    {
      return 'Nenhum item disponível';
    }

    if (filteredOptions.length === 0 && selectedIds.length === options.length)
    {
      return 'Todos os itens já foram adicionados';
    }

    if (filteredOptions.length === 0)
    {
      return emptyMessage;
    }

    return null;
  })();

  return (
    <div className="relative" ref={wrapperRef}>
      {(label || showBulkActions) && (
        <div className="mb-1 flex items-center justify-between gap-2">
          {label ? (
            <label className="block text-sm font-medium text-gray-700">{label}</label>
          ) : (
            <span />
          )}
          {showBulkActions && options.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                disabled={selectedIds.length === options.length}
              >
                Selecionar todos
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={handleClear}
                className="text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                disabled={selectedIds.length === 0}
              >
                Limpar
              </button>
            </div>
          )}
        </div>
      )}
      {hint && (
        <p className="mb-2 text-xs text-gray-500">{hint}</p>
      )}
      <div
        className={`relative min-h-[42px] w-full cursor-text rounded-md border bg-white px-3 py-2 pr-8 shadow-sm ${
          error ? 'border-red-300' : 'border-gray-300'
        } ${isOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'}`}
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        <div className="flex flex-wrap gap-1.5">
          {selectedOptions.map(option => (
            <span
              key={option.id}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-sm text-blue-800"
            >
              <span className="truncate">{option.name}</span>
              <button
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  handleRemove(option.id);
                }}
                className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full hover:bg-blue-100"
                aria-label={`Remover ${option.name}`}
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={event => {
              setSearchTerm(event.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={event => {
              if (event.key === 'Escape')
              {
                setIsOpen(false);
              }

              if (event.key === 'Backspace' && !searchTerm && selectedIds.length > 0)
              {
                handleRemove(selectedIds[selectedIds.length - 1]);
              }
            }}
            placeholder={selectedOptions.length === 0 ? placeholder : 'Buscar...'}
            className="min-w-[8rem] flex-1 border-0 bg-transparent p-0 text-sm focus:ring-0"
          />
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronDownIcon
            className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5">
          {dropdownMessage ? (
            <div className="px-3 py-2 text-gray-500">{dropdownMessage}</div>
          ) : (
            filteredOptions.map(option => (
              <button
                type="button"
                key={option.id}
                className="flex w-full flex-col px-3 py-2 text-left hover:bg-blue-50"
                onClick={() => handleSelect(option)}
              >
                <span className="truncate text-gray-900">{option.name}</span>
                {option.description && (
                  <span className="truncate text-xs text-gray-500">{option.description}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {selectedIds.length > 0 && (
        <p className="mt-1 text-xs text-gray-500">
          {selectedIds.length} {selectedIds.length === 1 ? 'selecionado' : 'selecionados'}
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default MultiSelect;
